import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, APP_INITIALIZER, Injector, NgModule } from '@angular/core';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i1 from '@angular/common/http';
import { HttpClientModule } from '@angular/common/http';

const CONFIGURATION_APP_INITIALIZER = new InjectionToken('CONFIGURATION_APP_INITIALIZER');
const CONFIGURATION_TYPE = new InjectionToken('CONFIGURATION_TYPE');
const CONFIGURATION_OPTIONS = new InjectionToken('CONFIGURATION_OPTIONS');

function isPromise(x) {
    return x != null && typeof x.then === 'function';
}

class ConfigurationService {
    constructor(http, platformLocation, injector, configurationType, configurationOptions) {
        this.http = http;
        this.platformLocation = platformLocation;
        this.injector = injector;
        this.configurationType = configurationType;
        this.configurationOptions = configurationOptions;
    }
    get configuration() {
        if (this.config == null) {
            throw Error('Configuration hasn\'t been initialized');
        }
        return this.config;
    }
    async init() {
        try {
            await this.initInternal();
        }
        catch (error) {
            throw Error(`Configuration load failed - ${error.message}`);
        }
    }
    async initInternal() {
        const urls = await this.getUrls();
        const externalUrls = urls.map(x => this.ensureExternalUrl(x));
        const promises = externalUrls.map(url => this.http.get(url).toPromise());
        await Promise.all(promises);
        let config = new this.configurationType();
        for (const promise of promises) {
            const value = await promise;
            for (const prop in value) {
                if (value.hasOwnProperty(prop)) {
                    // @ts-ignore
                    config[prop] = value[prop];
                }
            }
        }
        this.config = config;
        if (this.configurationOptions?.log === true) {
            console.log("Configuration loaded", config);
        }
    }
    async getUrls() {
        if (this.configurationOptions?.urlFactory == null) {
            return ['config.json'];
        }
        let result = this.configurationOptions.urlFactory(this.injector);
        if (isPromise(result)) {
            result = await result;
        }
        if (typeof result === 'string') {
            return [result];
        }
        if (Array.isArray(result)) {
            return result;
        }
        throw new Error('Unexpected value returned from ConfigurationUrlFactory');
    }
    ensureExternalUrl(url) {
        if (url.startsWith('//') || url.startsWith('http://') || url.startsWith('https://')) {
            return url;
        }
        if (url.startsWith('/')) {
            url = url.substr(1);
        }
        const baseHref = this.platformLocation.getBaseHrefFromDOM();
        const externalUrl = `${baseHref}${url}`;
        return externalUrl;
    }
}
ConfigurationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.0", ngImport: i0, type: ConfigurationService, deps: [{ token: i1.HttpClient }, { token: i2.PlatformLocation }, { token: i0.Injector }, { token: CONFIGURATION_TYPE }, { token: CONFIGURATION_OPTIONS }], target: i0.ɵɵFactoryTarget.Injectable });
ConfigurationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.1.0", ngImport: i0, type: ConfigurationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.0", ngImport: i0, type: ConfigurationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: i2.PlatformLocation }, { type: i0.Injector }, { type: i0.Type, decorators: [{
                    type: Inject,
                    args: [CONFIGURATION_TYPE]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CONFIGURATION_OPTIONS]
                }] }]; } });

class AngularRuntimeConfigModule {
    static forRoot(configurationType, options) {
        return {
            ngModule: AngularRuntimeConfigModule,
            providers: [
                ConfigurationService,
                {
                    provide: CONFIGURATION_TYPE,
                    useValue: configurationType
                },
                {
                    provide: CONFIGURATION_OPTIONS,
                    useValue: options
                },
                {
                    provide: configurationType,
                    useFactory: (configurationService) => configurationService.configuration,
                    deps: [ConfigurationService]
                },
                {
                    provide: APP_INITIALIZER,
                    useFactory: (configurationService, injector) => async () => {
                        // first we initialize configurations
                        await configurationService.init();
                        // then we run other initializations
                        const dispoAppInitializers = injector.get(CONFIGURATION_APP_INITIALIZER, []);
                        const all = dispoAppInitializers.map(p => p());
                        const promises = all.filter(x => isPromise(x));
                        if (promises.length !== 0) {
                            await Promise.all(promises);
                        }
                    },
                    deps: [ConfigurationService, Injector],
                    multi: true
                }
            ]
        };
    }
}
AngularRuntimeConfigModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.0", ngImport: i0, type: AngularRuntimeConfigModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AngularRuntimeConfigModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.1.0", ngImport: i0, type: AngularRuntimeConfigModule, imports: [CommonModule,
        HttpClientModule] });
AngularRuntimeConfigModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.1.0", ngImport: i0, type: AngularRuntimeConfigModule, imports: [CommonModule,
        HttpClientModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.0", ngImport: i0, type: AngularRuntimeConfigModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [
                        CommonModule,
                        HttpClientModule
                    ]
                }]
        }] });

/*
 * Public API Surface of angular-runtime-config
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AngularRuntimeConfigModule, CONFIGURATION_APP_INITIALIZER };
